import { Dimensions, Platform, useWindowDimensions } from 'react-native';

// A few px of slack so rounding between the window and screen frames can't
// register as "windowed" when the app is actually full-screen.
const TOLERANCE = 2;

/**
 * Detects whether the app is running in an iPad windowed context (Split View,
 * Slide Over, Stage Manager, or iPadOS 26 resizable windows) — i.e. the app no
 * longer occupies the full screen.
 *
 * iOS exposes no boolean for this, so we compare the window frame against the
 * screen frame. We compare longest-to-longest and shortest-to-shortest edges
 * (not raw width/height) so a device rotation — which swaps width and height on
 * both frames — can't transiently flip the result while the two update out of
 * sync. `useWindowDimensions` re-renders on resize/rotation; the screen frame is
 * effectively constant so a plain `Dimensions.get` is fine.
 */
export function useIsWindowed(): boolean {
  const win = useWindowDimensions();

  if (!(Platform.OS === 'ios' && Platform.isPad)) {
    return false;
  }

  const scr = Dimensions.get('screen');
  const winLong = Math.max(win.width, win.height);
  const winShort = Math.min(win.width, win.height);
  const scrLong = Math.max(scr.width, scr.height);
  const scrShort = Math.min(scr.width, scr.height);

  return winLong < scrLong - TOLERANCE || winShort < scrShort - TOLERANCE;
}
